import React from "react";
import { Alert, Box, Chip, LinearProgress, Typography } from "@mui/material";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import SectionHeader from "../components/common/SectionHeader";
import { tradingApi } from "../services/tradingApi";
import { marketDataApi } from "../services/marketDataApi";

const currency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(Number(value || 0));

const PositionsPage = () => {
  const { data: positions = [], isLoading: positionsLoading, error: positionsError } =
    tradingApi.endpoints.getPositions.useQuery();
  const { data: marketAssets = [], isLoading: assetsLoading } =
    marketDataApi.endpoints.getAllMarketAssets.useQuery();

  const assetLookup = React.useMemo(() => {
    return marketAssets.reduce((acc, item) => {
      acc[item.marketAssetId] = item;
      return acc;
    }, {});
  }, [marketAssets]);

  const rows = React.useMemo(() => {
    return positions.map((position) => {
      const asset = assetLookup[position.marketAssetId];
      return {
        ...position,
        symbol: asset?.symbol || `#${position.marketAssetId}`,
        assetName: asset?.name || "Unknown Asset",
        currentPrice: asset?.currentPrice ?? 0,
      };
    });
  }, [positions, assetLookup]);

  const columns = React.useMemo(
    () => [
      { field: "symbol", headerName: "Symbol", valueGetter: (row) => row.symbol },
      { field: "assetName", headerName: "Asset", valueGetter: (row) => row.assetName },
      {
        field: "quantity",
        headerName: "Quantity",
        align: "right",
        valueGetter: (row) => Number(row.quantity || 0),
      },
      {
        field: "averageCostBasis",
        headerName: "Avg Cost",
        align: "right",
        valueGetter: (row) => Number(row.averageCostBasis || 0),
        valueFormatter: (value) => currency(value),
      },
      {
        field: "marketValue",
        headerName: "Market Value",
        align: "right",
        valueGetter: (row) => Number(row.marketValue || 0),
        valueFormatter: (value) => currency(value),
      },
      {
        field: "unrealizedPnl",
        headerName: "Unrealized P/L",
        align: "right",
        valueGetter: (row) => Number(row.unrealizedPnl || 0),
        renderCell: (row) => (
          <Typography color={Number(row.unrealizedPnl) >= 0 ? "success.main" : "error.main"}>
            {currency(row.unrealizedPnl)}
          </Typography>
        ),
      },
      {
        field: "isClosed",
        headerName: "Status",
        valueGetter: (row) => (row.isClosed ? "Closed" : "Open"),
        renderCell: (row) => (
          <Chip
            size="small"
            color={row.isClosed ? "default" : "success"}
            label={row.isClosed ? "Closed" : "Open"}
          />
        ),
      },
      {
        field: "openedAt",
        headerName: "Opened",
        valueGetter: (row) => row.openedAt,
        valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
      },
    ],
    []
  );

  if (positionsLoading || assetsLoading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }

  if (positionsError) {
    return <Alert severity="error">Failed to load positions.</Alert>;
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <SectionHeader variant="h4" component="h1" gutterBottom sx={{ pb: 0.75 }}>
        Positions
      </SectionHeader>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Current holdings linked to tracked market assets.
      </Typography>
      <CommonDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.positionId}
        initialSortField="marketValue"
        initialSortDirection="desc"
        filterPlaceholder="Filter positions by symbol, asset, status..."
      />
    </Box>
  );
};

export default PositionsPage;
